// ============================================================================
// PairsForm — ChampDate : date saisissable au clavier + calendrier natif
// ============================================================================
// Retour FX 2026-10-13 : sur téléphone, un <input type="date"> natif ouvre le
// sélecteur et n'affiche JAMAIS le clavier (comportement Android / iOS, pas un
// défaut de l'app) — on est habitué à taper sa date de naissance. Ici : un
// champ texte JJ/MM/AAAA à clavier numérique (les « / » se posent seuls), plus
// un bouton calendrier qui ouvre le sélecteur natif quand le navigateur le
// permet (`showPicker`). La valeur échangée reste ISO AAAA-MM-JJ ("" = vide),
// comme avec l'ancien champ : aucun appelant n'a à changer son stockage.
// ============================================================================

import { useEffect, useRef, useState } from "react";
import { IconButton, InputAdornment, TextField, type TextFieldProps } from "@mui/material";
import { CalendarDays } from "lucide-react";

interface Props {
  label?: string;
  /** ISO AAAA-MM-JJ, ou "" / null quand vide. */
  value: string | null | undefined;
  onChange: (iso: string) => void;
  required?: boolean;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: TextFieldProps["size"];
  sx?: TextFieldProps["sx"];
  margin?: TextFieldProps["margin"];
  name?: string;
  autoFocus?: boolean;
  /** Bornes ISO transmises au sélecteur natif. */
  min?: string;
  max?: string;
  /** ex. "bday" pour une date de naissance. */
  autoComplete?: string;
}

/** AAAA-MM-JJ → JJ/MM/AAAA ("" si la valeur n'est pas une date ISO). */
function isoVersSaisie(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/** JJ/MM/AAAA → AAAA-MM-JJ, null si la date n'existe pas (31/02, 13e mois…). */
function saisieVersIso(saisie: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(saisie);
  if (!m) return null;
  const j = Number(m[1]);
  const mo = Number(m[2]);
  const a = Number(m[3]);
  if (a < 1900 || mo < 1 || mo > 12 || j < 1 || j > 31) return null;
  const d = new Date(Date.UTC(a, mo - 1, j));
  if (d.getUTCFullYear() !== a || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== j) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Pose les « / » au fil de la frappe : 8 chiffres au plus → JJ/MM/AAAA. */
function formaterSaisie(brut: string): string {
  const c = brut.replace(/\D/g, "").slice(0, 8);
  if (c.length <= 2) return c;
  if (c.length <= 4) return `${c.slice(0, 2)}/${c.slice(2)}`;
  return `${c.slice(0, 2)}/${c.slice(2, 4)}/${c.slice(4)}`;
}

type InputAvecPicker = HTMLInputElement & { showPicker?: () => void };

export default function ChampDate({
  label, value, onChange, required, helperText, error, disabled,
  fullWidth = true, size, sx, margin, name, autoFocus, min, max, autoComplete,
}: Props) {
  const [saisie, setSaisie] = useState(() => isoVersSaisie(value));
  const [invalide, setInvalide] = useState(false);
  const natifRef = useRef<HTMLInputElement>(null);
  // Le bouton calendrier n'apparaît que si le navigateur sait ouvrir le
  // sélecteur à la demande ; sinon la saisie au clavier suffit.
  const calendrierDisponible = typeof HTMLInputElement !== "undefined" && "showPicker" in HTMLInputElement.prototype;

  // Valeur poussée de l'extérieur (brouillon restauré, calendrier) → texte.
  useEffect(() => {
    if (saisieVersIso(saisie) !== (value || null)) {
      setSaisie(isoVersSaisie(value));
      setInvalide(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const changer = (brut: string) => {
    // Valeur ISO collée ou posée par un remplissage automatique (« bday ») :
    // on la lit telle quelle plutôt que de la hacher en JJ/MM/AAAA.
    const isoColle = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(brut);
    const txt = formaterSaisie(isoColle ? isoVersSaisie(brut.trim()) : brut);
    setSaisie(txt);
    if (txt.length === 10) {
      const iso = saisieVersIso(txt);
      setInvalide(!iso);
      if (iso) onChange(iso);
      else if (value) onChange("");
      return;
    }
    setInvalide(false);
    if (value) onChange("");
  };

  const ouvrirCalendrier = () => {
    const el = natifRef.current as InputAvecPicker | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") el.showPicker();
      else el.click();
    } catch {
      el.click();
    }
  };

  return (
    <TextField
      label={label}
      value={saisie}
      onChange={(e) => changer(e.target.value)}
      onBlur={() => setInvalide(saisie.length > 0 && !saisieVersIso(saisie))}
      placeholder="JJ/MM/AAAA"
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      margin={margin}
      name={name}
      autoFocus={autoFocus}
      error={!!error || invalide}
      helperText={invalide ? "Date invalide — format attendu JJ/MM/AAAA" : helperText}
      inputProps={{ inputMode: "numeric", maxLength: 10, autoComplete: autoComplete ?? "off" }}
      InputProps={{
        endAdornment: calendrierDisponible ? (
          <InputAdornment position="end">
            <IconButton aria-label="Ouvrir le calendrier" onClick={ouvrirCalendrier} edge="end" size="small" disabled={disabled}>
              <CalendarDays size={18} />
            </IconButton>
            {/* Sélecteur natif : présent dans le DOM (showPicker l'exige), invisible. */}
            <input
              ref={natifRef}
              type="date"
              tabIndex={-1}
              aria-hidden="true"
              value={value || ""}
              min={min}
              max={max}
              onChange={(e) => onChange(e.target.value)}
              style={{ position: "absolute", right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            />
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}
